import type { TrackerReviewStatus, TrackerTask } from './tracker-types.js';

export type TrackerReviewFilter = 'all' | 'unreviewed' | 'approved' | 'rejected';

type ReviewableTask = Pick<TrackerTask, 'postingEventId' | 'reviewStatus'>;

/**
 * A posting event is the durable server-side submission boundary that can be
 * approved or rejected. Assignment/task status is display context only and
 * must not hide an event that already exists.
 */
export function isTrackerTaskReviewable(task: Pick<TrackerTask, 'postingEventId'>): boolean {
    return Number.isInteger(task.postingEventId) && task.postingEventId > 0;
}

export function buildTrackerReviewSummary(tasks: ReviewableTask[]) {
    const reviewableTasks = tasks.filter(isTrackerTaskReviewable);

    return {
        total: reviewableTasks.length,
        approved: reviewableTasks.filter((task) => task.reviewStatus === 'approved').length,
        rejected: reviewableTasks.filter((task) => task.reviewStatus === 'rejected').length,
        pending: reviewableTasks.filter((task) => task.reviewStatus === 'pending').length,
    };
}

export function filterTrackerTasksByReview<T extends ReviewableTask>(
    tasks: T[],
    filter: TrackerReviewFilter,
): T[] {
    if (filter === 'all') return tasks;

    const targetStatus: TrackerReviewStatus = filter === 'unreviewed' ? 'pending' : filter;
    return tasks.filter(
        (task) => isTrackerTaskReviewable(task) && task.reviewStatus === targetStatus,
    );
}

export function buildTrackerReviewFilterCounts(tasks: ReviewableTask[]) {
    const summary = buildTrackerReviewSummary(tasks);

    return {
        all: tasks.length,
        unreviewed: summary.pending,
        approved: summary.approved,
        rejected: summary.rejected,
    };
}
